<template>
  <section class="pb-48">
    <div class="container">
      <div class="py-10" v-if="ambassador">
        <div class="flex flex-col md:flex-row items-center md:items-start gap-8 mb-10">
          <div class="flex-shrink-0">
            <NuxtImg
              :src="ambassador.image"
              class="w-48 h-48 rounded-full mx-auto"
              alt=""
            />
          </div>
          <div class="flex-1 text-center md:text-start">
            <UISectionTitle class="mb-3">{{ ambassador.name }}</UISectionTitle>
            <div class="flex flex-wrap gap-4 items-center justify-center md:justify-start text-fourth mb-4">
              <span v-if="ambassador.category" class="px-4 py-2 bg-[#F5F5F5] rounded-lg">
                {{ ambassador.category }}
              </span>
            </div>
            
            <!-- Instagram -->
            <div class="mb-4 flex items-center gap-2 justify-center md:justify-start">
              <font-awesome :icon="['fab', 'instagram']" class="text-xl text-third" />
              <template v-if="ambassador.instagram && ambassador.instagram !== 'Soon' && ambassador.instagram.trim() !== ''">
                <a
                  :href="ambassador.instagram.startsWith('http') ? ambassador.instagram : 'https://instagram.com/' + ambassador.instagram.replace('@', '')"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-third hover:underline font-medium"
                >
                  {{ ambassador.instagram.replace('@', '') }}
                </a>
              </template>
              <span v-else-if="ambassador.instagram" class="text-fourth font-medium">{{ ambassador.instagram }}</span>
            </div>
            
            <!-- Coupon Code -->
            <div class="mb-4 flex items-center gap-2 justify-center md:justify-start">
              <font-awesome :icon="['fas', 'tag']" class="text-xl text-third" />
              <span v-if="ambassador.coupon" class="text-third font-medium">{{ ambassador.coupon }}</span>
            </div>
            
            <!-- Description -->
            <div v-if="ambassador.description" class="text-fourth mt-6" v-html="ambassador.description"></div>
          </div>
        </div>

        <!-- Trips Section -->
        <div v-if="allTrips.length > 0" class="mt-12">
          <!-- Red Divider with Text -->
          <div class="flex items-center justify-center mb-8">
            <div class="flex-1 border-t" style="border-color: #F75D37;"></div>
            <span class="px-4 text-xl font-semibold text-first">{{ t("detail_ambassador.my_trip_recommendations") }}</span>
            <div class="flex-1 border-t" style="border-color: #F75D37;"></div>
          </div>
          <div class="grid md:grid-cols-4 mintab:grid-cols-2 mob:grid-cols-1 gap-7">
            <div v-for="trip in allTrips" :key="trip.id">
              <UITourCard :tour="trip" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
const { t } = useI18n();
const props = defineProps(["ambassadorID"]);
const ambassadorID = ref(props.ambassadorID);
const BaseUrl = inject("BaseUrl") as string;
const ambassador = ref<Ambassador | null>(null);

// Fetch ambassador data
const { data } = await useAuthFetch<Api>(`${BaseUrl}/ambassador/${ambassadorID.value}`);
ambassador.value = data.value?.data.brandAmbassador as Ambassador;

// Track profile view
if (ambassador.value) {
  try {
    await useAuthFetch(`${BaseUrl}/profile-views`, {
      method: 'POST',
      body: {
        viewable_type: 'App\\Models\\BrandAmbassador',
        viewable_id: ambassador.value.id,
      },
    });
  } catch (error) {
    // Silently fail - tracking should not break the page
    console.error('Failed to track profile view:', error);
  }
}

// Helper function to normalize trip data with default values
const normalizeTrip = (trip: Record<string, unknown>): Trip => {
  return {
    ...trip,
    continent: trip.continent || { id: 0, name: "" } as Continent,
    country: trip.country || { id: 0, name: "" } as Country,
    month: trip.month || { id: 0, name: "" } as Month,
    gallery: trip.gallery || [],
    itinerary: trip.itinerary || [],
    trip_start: trip.trip_start || "",
    trip_end: trip.trip_end || "",
    the_price_includes: trip.the_price_includes || "",
    the_price_does_not_include: trip.the_price_does_not_include || "",
    andventures_and_site_that_will_be_covered: trip.andventures_and_site_that_will_be_covered || "",
    accommodation_type: trip.accommodation_type || "",
    level_of_difficulty: trip.level_of_difficulty || "",
    theme: trip.theme || "",
    guide: trip.guide || "",
    chat_url: trip.chat_url || "",
    is_trending: trip.is_trending || 0,
  };
};


// Combine all trips from different arrays
const allTrips = computed(() => {
  if (!ambassador.value) return [];
  
  const trips: Trip[] = [];
  
  // Add trips from main trips array
  if (ambassador.value.trips) {
    trips.push(...ambassador.value.trips.map(normalizeTrip));
  }
  
  // Add create_trips if they exist
  if (ambassador.value.create_trips) {
    trips.push(...ambassador.value.create_trips.map(normalizeTrip));
  }
  
  // Add private_trips if they exist
  if (ambassador.value.private_trips) {
    trips.push(...ambassador.value.private_trips.map(normalizeTrip));
  }
  
  return trips;
});

// SEO - Update when ambassador data is loaded
watch(
  () => ambassador.value,
  (newAmbassador) => {
    if (newAmbassador) {
      useSeoMeta({
        title: newAmbassador.name,
        description: t("ambassador.seo.description"),
        ogTitle: newAmbassador.name,
        ogDescription: t("ambassador.seo.description"),
        ogImage: newAmbassador.image || "/favicon.ico",
        ogUrl: "[og:url]",
        twitterTitle: newAmbassador.name,
        twitterDescription: t("ambassador.seo.description"),
        twitterImage: newAmbassador.image || "/favicon.ico",
        twitterCard: "summary",
      });
    }
  },
  { immediate: true }
);
</script>

